import { cn } from '@/lib/utils';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { Region } from '@chiroup/core/types/Condition.type';
import { Visit } from '@chiroup/core/types/PatientVisit.type';
import React, { useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { REGION_OPTIONS, ROM_TESTS_PER_REGION } from '../../../../../constants';
import RangeOfMotionRegion from './RangeOfMotionRegion';

type Props = {
  close?: () => void;
  value: {
    [key: string]: {
      [key: string]: { [key: string]: { value?: number; pain?: boolean } };
    };
  };
  onChange: (
    region: string,
    prop: string,
    val: {
      value?: number;
      pain?: boolean;
      side?: 'Right' | 'Left' | 'Both' | 'SpinalArea';
    }
  ) => void;
  onRemoveRegion: (region: string) => void;
  setVisitForm: React.Dispatch<React.SetStateAction<Visit>>;
  complaintId: string;
  romSliderStep: number;
  signed?: boolean;
};

const RangeOfMotion: React.FC<Props> = ({
  close,
  value,
  onChange,
  onRemoveRegion,
  setVisitForm,
  complaintId,
  romSliderStep,
  signed = false,
}) => {
  const [currentTab, setCurrentTab] = useState<Region>();

  const possibleTabs = useMemo(() => {
    return REGION_OPTIONS.filter(region => !!ROM_TESTS_PER_REGION[region.value]).map(region => ({
      name: region.text,
      region: region.value,
    }));
  }, []);

  const tabs = useMemo(() => {
    const tabsInUse = possibleTabs.filter(region => !!value?.[region.region]);

    if (tabsInUse?.length) {
      return tabsInUse;
    }

    return [];
  }, [possibleTabs, value]);

  useEffect(() => {
    if (!currentTab) {
      if (tabs?.length) {
        setCurrentTab(tabs[0]?.region);
      } else {
        setCurrentTab(possibleTabs[0]?.region);
      }
    }
  }, [currentTab, possibleTabs, tabs]);

  const tabsLeft = useMemo(() => {
    return possibleTabs.filter(region => !value?.[region.region]);
  }, [possibleTabs, value]);

  const removeRegion = () => {
    onRemoveRegion(currentTab as string);
    setCurrentTab(tabs.find(tab => tab.region !== currentTab)?.region);
  };

  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto">
        <Scrollbars className="min-h-14 w-full" autoHide>
          <div>
            <div className="sm:hidden">
              <label htmlFor="tabs" className="sr-only">
                Select a tab
              </label>
              <select
                id="tabs"
                name="tabs"
                className="block w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                defaultValue={tabs.find(tab => tab.region === currentTab)?.name}
                onChange={e => {
                  setCurrentTab(e.target.value as Region);
                }}
                value={tabs.find(tab => tab.region === currentTab)?.region}
              >
                {possibleTabs?.map(tab => (
                  <option key={tab.name} value={tab.region}>
                    {tab.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="hidden sm:block">
              <nav className="flex space-x-4" aria-label="Tabs">
                {(tabs || []).map(tab => (
                  <div
                    key={tab.name}
                    className={cn(
                      currentTab === tab.region
                        ? 'bg-white dark:bg-dark-gray-900 border-gray-300 dark:border-dark-gray-700 text-gray-700 dark:text-dark-gray-50'
                        : 'text-gray-500 hover:text-gray-700 dark:text-dark-gray-50 dark:hover:text-dark-gray-100 border-transparent',
                      'px-3 py-2 font-medium text-sm rounded-md cursor-pointer whitespace-nowrap border'
                    )}
                    aria-current={currentTab === tab.region ? 'page' : undefined}
                    onClick={() => setCurrentTab(tab.region)}
                  >
                    {tab.name}
                  </div>
                ))}
                {(tabsLeft || []).map(tab => (
                  <div
                    key={tab.name}
                    className={cn(
                      currentTab === tab.region
                        ? 'bg-gray-100 text-gray-700 dark:bg-dark-gray-800 dark:text-dark-gray-100'
                        : 'bg-gray-100 text-gray-300 hover:text-gray-400 dark:bg-dark-gray-700 dark:text-dark-gray-500',
                      'px-3 py-2 font-medium text-sm rounded-md cursor-pointer whitespace-nowrap border border-transparent'
                    )}
                    aria-current={currentTab === tab.region ? 'page' : undefined}
                    onClick={() => setCurrentTab(tab.region)}
                  >
                    {tab.name}
                  </div>
                ))}
              </nav>
            </div>
          </div>
        </Scrollbars>
      </div>
      {currentTab ? (
        <RangeOfMotionRegion
          rows={ROM_TESTS_PER_REGION[currentTab]}
          onChange={onChange}
          currentTab={currentTab}
          value={value}
          setVisitForm={setVisitForm}
          complaintId={complaintId}
          romSliderStep={romSliderStep}
          signed={signed}
        />
      ) : null}
      <div className="flex flex-row justify-between">
        <Button onClick={removeRegion} text="Remove region" color={ButtonColors.plainWithBorder} />
        {!!close && <Button onClick={close} text="Close" color={ButtonColors.plainWithBorder} />}
      </div>
    </div>
  );
};

export default RangeOfMotion;

//abbott lyon
